import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  TrendingUp, TrendingDown, Clock, Calendar, Mail, Users, 
  CheckCircle2, AlertTriangle, Lightbulb, BarChart3, Activity,
  RefreshCw, Target, Zap, Coffee, Moon, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Microsoft logo component
function MicrosoftLogo({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

// Circular progress component
function CircularProgress({ value, size = 120, strokeWidth = 10, label, sublabel }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;
  
  const getColor = () => {
    if (value >= 70) return '#10B981'; // Green
    if (value >= 50) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };
  
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-muted/20"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-3xl font-bold">{Math.round(value)}</span>
        {label && <span className="text-xs text-muted-foreground">{label}</span>}
        {sublabel && <span className="text-xs text-muted-foreground">{sublabel}</span>}
      </div>
    </div>
  );
}

// Alert component
function ProductivityAlert({ alert }) {
  const icons = {
    danger: AlertTriangle,
    warning: AlertTriangle,
    info: Lightbulb,
  };
  const Icon = icons[alert.type] || Lightbulb;
  
  const colors = {
    danger: 'border-rose-500/50 bg-rose-500/5 text-rose-600',
    warning: 'border-amber-500/50 bg-amber-500/5 text-amber-600',
    info: 'border-blue-500/50 bg-blue-500/5 text-blue-600',
  };
  
  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg border ${colors[alert.type]}`}>
      <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
      <p className="text-sm">{alert.message}</p>
    </div>
  );
}

// Insight card
function InsightCard({ insight }) {
  const icons = {
    clock: Clock,
    'check-circle': CheckCircle2,
    users: Users,
    mail: Mail,
    'trending-up': TrendingUp,
    calendar: Calendar,
  };
  const Icon = icons[insight.icon] || Activity;
  
  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h4 className="font-semibold">{insight.title}</h4>
            <p className="text-sm text-muted-foreground">{insight.description}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Recommendation card
function RecommendationCard({ rec, index }) {
  const priorityColors = {
    high: 'bg-rose-500',
    medium: 'bg-amber-500',
    low: 'bg-blue-500',
  };
  
  return (
    <div className="flex items-start gap-4 p-4 border rounded-lg hover:shadow-sm transition-all">
      <div className={`w-1 h-full min-h-[60px] rounded-full ${priorityColors[rec.priority]}`} />
      <div className="flex-1">
        <h4 className="font-semibold">{rec.title}</h4>
        <p className="text-sm text-muted-foreground mb-2">{rec.description}</p>
        {rec.action && (
          <Badge variant="outline" className="text-xs">{rec.action}</Badge>
        )}
      </div>
    </div>
  );
}

// Team member row
function TeamMemberRow({ member, rank }) {
  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-500';
    if (score >= 50) return 'text-amber-500';
    return 'text-rose-500';
  };
  
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div className="flex items-center gap-3">
        {rank <= 3 && (
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
            rank === 1 ? 'bg-amber-400 text-amber-900' :
            rank === 2 ? 'bg-slate-300 text-slate-700' :
            'bg-amber-600 text-amber-100'
          }`}>
            {rank}
          </div>
        )}
        {rank > 3 && <div className="w-6 h-6" />}
        <span className="font-medium">{member.name}</span>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <span className="text-muted-foreground">{member.tasks_completed} completadas</span>
        {member.tasks_overdue > 0 && (
          <Badge variant="outline" className="text-rose-500">{member.tasks_overdue} vencidas</Badge>
        )}
        <span className={`font-bold ${getScoreColor(member.productivity_score)}`}>
          {member.productivity_score}%
        </span>
      </div>
    </div>
  );
}

export default function Productivity() {
  const { user, token } = useAuth();
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [personalData, setPersonalData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [teamDashboard, setTeamDashboard] = useState(null);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [personalRes, teamRes, insightsRes] = await Promise.all([
        fetch(`${API}/api/productivity/personal?period=${period}`, { headers }),
        fetch(`${API}/api/productivity/team?period=${period}`, { headers }),
        fetch(`${API}/api/productivity/insights`, { headers }),
      ]);
      
      if (personalRes.ok) {
        setPersonalData(await personalRes.json());
      }
      if (teamRes.ok) {
        setTeamData(await teamRes.json());
      }
      if (insightsRes.ok) {
        setInsightsData(await insightsRes.json());
      }

      // Load team dashboard (admin only)
      try {
        const dashRes = await fetch(`${API}/api/productivity/team-dashboard?period=${period}`, { headers });
        if (dashRes.ok) {
          setTeamDashboard(await dashRes.json());
        }
      } catch (e) {
        // Non-admin users will get 403, that's expected
      }
    } catch (err) {
      console.error('Error loading productivity data:', err);
      toast.error('Error al cargar datos de productividad');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    await loadData();
    setSyncing(false);
    toast.success('Datos actualizados');
  };

  const handleExportProductivity = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API}/api/productivity/export/excel?period=${period}`, { headers });
      if (!res.ok) {
        toast.error('Error al exportar (solo administradores)');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `productividad_equipo_${period}_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Reporte de productividad exportado');
    } catch (err) {
      toast.error('Error al exportar productividad');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const metrics = personalData?.metrics || {};
  const teamMetrics = teamData?.metrics || {};
  const insights = insightsData?.insights || [];
  const recommendations = insightsData?.recommendations || [];
  const allAlerts = [...(personalData?.alerts || []), ...(teamData?.alerts || [])];

  // Calculate focus ratio for display
  const totalHours = metrics.meeting_hours + metrics.focus_hours;
  const focusPercent = totalHours > 0 ? (metrics.focus_hours / totalHours) * 100 : 50;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="productivity-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight flex items-center gap-3">
            <Activity className="h-8 w-8 text-primary" />
            Productividad
          </h1>
          <div className="text-muted-foreground flex items-center gap-2">
            Métricas estilo Viva Insights
            {personalData?.microsoft_connected && (
              <Badge variant="outline" className="gap-1">
                <MicrosoftLogo className="h-3 w-3" />
                Conectado
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Última semana</SelectItem>
              <SelectItem value="month">Último mes</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={handleSync} disabled={syncing}>
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {allAlerts.length > 0 && (
        <div className="space-y-2">
          {allAlerts.slice(0, 3).map((alert, idx) => (
            <ProductivityAlert key={idx} alert={alert} />
          ))}
        </div>
      )}

      {/* Main Metrics Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Collaboration Score */}
        <Card className="row-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Score de Colaboración
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center pt-4">
            <CircularProgress 
              value={metrics.collaboration_score || 0} 
              label="puntos"
              sublabel="de 100"
            />
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Basado en reuniones, emails y tareas completadas
            </p>
          </CardContent>
        </Card>

        {/* Meeting Hours */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Horas en Reuniones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{metrics.meeting_hours || 0}h</p>
            <p className="text-sm text-muted-foreground">
              {metrics.meetings_attended || 0} reuniones esta {period === 'week' ? 'semana' : 'mes'}
            </p>
          </CardContent>
        </Card>

        {/* Focus Hours */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Target className="h-4 w-4" />
              Tiempo Enfocado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-emerald-500">{metrics.focus_hours || 0}h</p>
            <p className="text-sm text-muted-foreground">
              {Math.round(focusPercent)}% del tiempo de trabajo
            </p>
          </CardContent>
        </Card>

        {/* Focus vs Meetings Bar */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Balance de Tiempo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1">
                  <Coffee className="h-3 w-3" /> Enfoque
                </span>
                <span className="flex items-center gap-1">
                  Reuniones <Calendar className="h-3 w-3" />
                </span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                <div 
                  className="bg-emerald-500 transition-all"
                  style={{ width: `${focusPercent}%` }}
                />
                <div 
                  className="bg-blue-500 transition-all"
                  style={{ width: `${100 - focusPercent}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{Math.round(focusPercent)}%</span>
                <span>{Math.round(100 - focusPercent)}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Emails */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Emails
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div>
                <p className="text-2xl font-bold">{metrics.emails_sent || 0}</p>
                <p className="text-xs text-muted-foreground">Enviados</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{metrics.emails_received || 0}</p>
                <p className="text-xs text-muted-foreground">Recibidos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tasks */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Tareas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div>
                <p className="text-2xl font-bold text-emerald-500">{metrics.tasks_completed || 0}</p>
                <p className="text-xs text-muted-foreground">Completadas</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-500">{metrics.tasks_in_progress || 0}</p>
                <p className="text-xs text-muted-foreground">En progreso</p>
              </div>
              {metrics.tasks_overdue > 0 && (
                <div>
                  <p className="text-2xl font-bold text-rose-500">{metrics.tasks_overdue}</p>
                  <p className="text-xs text-muted-foreground">Vencidas</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Meeting Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Distribución de Reuniones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="text-center">
                <div className="flex items-center gap-1 text-amber-500">
                  <Zap className="h-4 w-4" />
                  <span className="text-lg font-bold">{metrics.meeting_distribution?.morning || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Mañana</p>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1 text-blue-500">
                  <Clock className="h-4 w-4" />
                  <span className="text-lg font-bold">{metrics.meeting_distribution?.afternoon || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Tarde</p>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1 text-violet-500">
                  <Moon className="h-4 w-4" />
                  <span className="text-lg font-bold">{metrics.meeting_distribution?.evening || 0}</span>
                </div>
                <p className="text-xs text-muted-foreground">Noche</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Team Dashboard, Insights, and Team */}
      <Tabs defaultValue={teamDashboard ? "team-dashboard" : "insights"} className="space-y-4">
        <TabsList>
          {teamDashboard && (
            <TabsTrigger value="team-dashboard" className="gap-2" data-testid="team-dashboard-tab">
              <BarChart3 className="h-4 w-4" />
              Dashboard Equipo
            </TabsTrigger>
          )}
          <TabsTrigger value="insights" className="gap-2">
            <Lightbulb className="h-4 w-4" />
            Insights
          </TabsTrigger>
          <TabsTrigger value="team" className="gap-2">
            <Users className="h-4 w-4" />
            Equipo
          </TabsTrigger>
        </TabsList>

        {/* Team Dashboard Tab */}
        {teamDashboard && (
          <TabsContent value="team-dashboard" className="space-y-6" data-testid="team-dashboard-content">
            {/* Header with sync and export */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {teamDashboard.last_updated && (
                  <span>Actualizado: {new Date(teamDashboard.last_updated).toLocaleString('es-DO')}</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={async () => {
                  try {
                    const headers = { Authorization: `Bearer ${token}` };
                    await fetch(`${API}/api/productivity/sync`, { method: 'POST', headers });
                    toast.success('Sincronizando datos de Microsoft...');
                    setTimeout(() => loadData(), 1000);
                  } catch (e) { toast.error('Error al sincronizar'); }
                }} data-testid="sync-productivity-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sincronizar
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportProductivity} data-testid="export-productivity-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Exportar Excel
                </Button>
              </div>
            </div>

            {/* Team Dashboard Alerts */}
            {teamDashboard.alerts?.length > 0 && (
              <div className="space-y-2">
                {teamDashboard.alerts.map((alert, idx) => (
                  <ProductivityAlert key={idx} alert={alert} />
                ))}
              </div>
            )}

            {/* Team Totals */}
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Reuniones Total</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{teamDashboard.team_totals?.total_meeting_hours || 0}h</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Enfoque Total</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-500">{teamDashboard.team_totals?.total_focus_hours || 0}h</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Emails</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{teamDashboard.team_totals?.total_emails || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tareas Completadas</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-emerald-500">{teamDashboard.team_totals?.total_tasks_completed || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tareas Vencidas</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-rose-500">{teamDashboard.team_totals?.total_tasks_overdue || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Score Promedio</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-primary">{teamDashboard.team_totals?.avg_collaboration_score || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Microsoft Connection Status */}
            {!teamDashboard.microsoft_app_connected && (
              <Card className="border-dashed border-amber-500/50 bg-amber-500/5">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-sm">Permisos de Microsoft 365 no configurados</p>
                      <p className="text-xs text-muted-foreground">Las métricas de reuniones y emails requieren permisos de aplicación en Azure AD. Los datos mostrados se basan en tareas locales únicamente.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Individual Collaborators */}
            <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-3">
              {teamDashboard.collaborators?.map((collab, idx) => (
                <Card key={collab.email} className="hover:shadow-md transition-all" data-testid={`collaborator-card-${idx}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx === 0 ? 'bg-amber-100 text-amber-700' :
                          idx === 1 ? 'bg-slate-100 text-slate-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {collab.name.charAt(0)}
                        </div>
                        <div>
                          <CardTitle className="text-base">{collab.name}</CardTitle>
                          <p className="text-xs text-muted-foreground">{collab.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${
                          collab.collaboration_score >= 70 ? 'text-emerald-500' :
                          collab.collaboration_score >= 50 ? 'text-amber-500' :
                          'text-rose-500'
                        }`}>{collab.collaboration_score}</p>
                        <p className="text-xs text-muted-foreground">Score</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Metrics grid */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-blue-500" />
                        <span className="text-muted-foreground">Reuniones:</span>
                        <span className="font-semibold ml-auto">{collab.meeting_hours}h</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Target className="h-3.5 w-3.5 text-emerald-500" />
                        <span className="text-muted-foreground">Enfoque:</span>
                        <span className="font-semibold ml-auto">{collab.focus_hours}h</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-violet-500" />
                        <span className="text-muted-foreground">Emails:</span>
                        <span className="font-semibold ml-auto">{collab.emails_sent + collab.emails_received}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                        <span className="text-muted-foreground">Completadas:</span>
                        <span className="font-semibold ml-auto">{collab.tasks_completed}</span>
                      </div>
                    </div>

                    {/* Task progress bar */}
                    <div>
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Tareas</span>
                        <span>{collab.tasks_completed} completadas / {collab.tasks_in_progress} en progreso{collab.tasks_overdue > 0 ? ` / ${collab.tasks_overdue} vencidas` : ''}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden flex">
                        {(() => {
                          const total = collab.tasks_completed + collab.tasks_in_progress + collab.tasks_overdue;
                          if (total === 0) return <div className="bg-muted w-full" />;
                          return (
                            <>
                              <div className="bg-emerald-500" style={{ width: `${(collab.tasks_completed / total) * 100}%` }} />
                              <div className="bg-amber-500" style={{ width: `${(collab.tasks_in_progress / total) * 100}%` }} />
                              <div className="bg-rose-500" style={{ width: `${(collab.tasks_overdue / total) * 100}%` }} />
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Meeting distribution */}
                    <div className="flex gap-3 text-xs">
                      <div className="flex items-center gap-1">
                        <Zap className="h-3 w-3 text-amber-500" />
                        <span>{collab.meeting_distribution?.morning || 0} AM</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-blue-500" />
                        <span>{collab.meeting_distribution?.afternoon || 0} PM</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Moon className="h-3 w-3 text-violet-500" />
                        <span>{collab.meeting_distribution?.evening || 0} Noche</span>
                      </div>
                    </div>

                    {/* Connection badge */}
                    {collab.microsoft_connected ? (
                      <Badge variant="outline" className="gap-1 text-xs">
                        <MicrosoftLogo className="h-3 w-3" />
                        Microsoft conectado
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 text-xs text-muted-foreground">
                        Solo datos locales
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Insights */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  Insights
                </CardTitle>
                <CardDescription>Análisis de tu productividad</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {insights.length > 0 ? (
                  insights.map((insight, idx) => (
                    <InsightCard key={idx} insight={insight} />
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    Conecta Microsoft 365 para obtener insights
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-500" />
                  Recomendaciones
                </CardTitle>
                <CardDescription>Acciones para mejorar</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {recommendations.length > 0 ? (
                  recommendations.map((rec, idx) => (
                    <RecommendationCard key={idx} rec={rec} index={idx} />
                  ))
                ) : (
                  <div className="text-center py-4">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                    <p className="text-muted-foreground">¡Todo está bien! No hay recomendaciones pendientes.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            {/* Team Stats */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Miembros del Equipo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{teamMetrics.total_members || 0}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Tareas Completadas (Equipo)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-emerald-500">{teamMetrics.total_tasks_completed || 0}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Promedio por Miembro
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{teamMetrics.avg_tasks_per_member || 0}</p>
                <p className="text-sm text-muted-foreground">tareas completadas</p>
              </CardContent>
            </Card>
          </div>

          {/* Top Performers */}
          {teamData?.is_admin && teamMetrics.top_performers?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  Ranking de Productividad
                </CardTitle>
                <CardDescription>Top performers del equipo</CardDescription>
              </CardHeader>
              <CardContent>
                {teamMetrics.top_performers.map((member, idx) => (
                  <TeamMemberRow key={member.user_id} member={member} rank={idx + 1} />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Workload Distribution */}
          {teamData?.is_admin && teamMetrics.workload_distribution?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-500" />
                  Distribución de Carga de Trabajo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {teamMetrics.workload_distribution.map((member) => (
                    <div key={member.user_id} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>{member.name}</span>
                        <span className="text-muted-foreground">{member.total_tasks} tareas</span>
                      </div>
                      <Progress 
                        value={teamMetrics.workload_distribution.length > 0 
                          ? (member.total_tasks / Math.max(...teamMetrics.workload_distribution.map(m => m.total_tasks))) * 100
                          : 0
                        } 
                        className="h-2"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {!teamData?.is_admin && (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Métricas de Equipo Limitadas</h3>
                <p className="text-sm text-muted-foreground">
                  Solo los administradores pueden ver el ranking completo y la distribución de carga de trabajo.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Microsoft Connection Notice */}
      {!personalData?.microsoft_connected && (
        <Card className="border-dashed border-blue-500/50 bg-blue-500/5">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <MicrosoftLogo className="h-12 w-12" />
              <div className="flex-1">
                <h3 className="font-semibold">Conecta Microsoft 365 para más insights</h3>
                <p className="text-sm text-muted-foreground">
                  Obtén métricas de reuniones, emails y colaboración conectando tu cuenta de Microsoft.
                </p>
              </div>
              <Button variant="outline" onClick={() => window.location.href = '/settings'}>
                Ir a Configuración
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
